import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { signInWithRedirect } from "aws-amplify/auth";
import { Bell, Loader2 } from "lucide-react";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useWebPush } from "@/hooks/useWebPush";
import { useNativePush } from "@/hooks/useNativePush";
import { NotificationTriggers, WebPushCard, NativePushCard, TimingPreferences, QuietHours, LocationPreferences, DailyLimit } from "@/components/notification";

export default function NotificationSettings() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    mockLogin,
    isDemoModeAvailable,
    isDemoMode,
    user,
  } = useAuth();
  const {
    preferences,
    isLoading: prefsLoading,
    updatePreferences,
    initializePreferences,
    isCreating,
    error: preferencesError,
    createError,
  } = useNotificationPreferences({
    userId: user?.id,
    enabled: isAuthenticated && !isDemoMode,
  });

  const updatePreferencesSafely = useCallback(
    (updates: Record<string, unknown>) => {
      if (isDemoMode) return;
      updatePreferences(updates);
    },
    [isDemoMode, updatePreferences]
  );

  // Web push logic extracted to hook
  const {
    pushSupported,
    pushPermission,
    isSubscribing,
    subscriptionError,
    requestPushPermission,
    disablePushNotifications,
  } = useWebPush({ updatePreferences: updatePreferencesSafely, preferences });

  // Native push logic extracted to hook
  const {
    isNativePlatform,
    nativePushAvailable,
    nativePushPermission,
    nativePushEnabled,
    isNativeSubscribing,
    enableNativePush,
    disableNativePush,
  } = useNativePush({ userId: user?.id });

  // Initialize preferences for new users
  useEffect(() => {
    if (
      isAuthenticated &&
      !isDemoMode &&
      user?.id &&
      !prefsLoading &&
      !isCreating &&
      !preferences &&
      !preferencesError &&
      !createError
    ) {
      void initializePreferences().catch(() => undefined);
    }
  }, [
    createError,
    initializePreferences,
    isAuthenticated,
    isCreating,
    isDemoMode,
    preferences,
    preferencesError,
    prefsLoading,
    user?.id,
  ]);

  // Native/web push functions now provided by hooks

  const handleToggle = (field: string, value: boolean) => {
    updatePreferencesSafely({ [field]: value });
    toast.success("Preference updated");
  };

  if (authLoading || (isAuthenticated && prefsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4 card-elevated border-none shadow-premium">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Bell className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Sign In Required</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground text-lg">
              Sign in to manage your notification preferences
            </p>
            <div className="space-y-3">
              <Button
                size="lg"
                onClick={() => signInWithRedirect()}
                className="w-full"
              >
                Sign In with AWS Cognito
              </Button>
              {isDemoModeAvailable && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => mockLogin()}
                    className="w-full"
                  >
                    Continue as Demo User
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header */}
      <section className="relative py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="container text-center">
          <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
            <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl">
              <Bell className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold">Notification Settings</h1>
            <p className="text-lg sm:text-xl text-muted-foreground">
              Get notified when exceptional sunrise and sunset conditions are predicted
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container py-8 sm:py-10 md:py-12">
        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
          <WebPushCard
            pushSupported={pushSupported}
            pushPermission={pushPermission}
            isSubscribing={isSubscribing}
            subscriptionError={subscriptionError}
            preferences={preferences}
            onEnable={requestPushPermission}
            onDisable={disablePushNotifications}
          />

          <NativePushCard
            isNativePlatform={isNativePlatform}
            nativePushAvailable={nativePushAvailable}
            nativePushPermission={nativePushPermission}
            nativePushEnabled={nativePushEnabled}
            isNativeSubscribing={isNativeSubscribing}
            onEnable={enableNativePush}
            onDisable={disableNativePush}
          />

          <NotificationTriggers preferences={preferences} onToggle={handleToggle} />

          <TimingPreferences
            preferences={preferences}
            onUpdate={updatePreferencesSafely}
          />

          <QuietHours
            preferences={preferences}
            onUpdate={updatePreferencesSafely}
          />

          <LocationPreferences
            preferences={preferences}
            onUpdate={updatePreferencesSafely}
          />

          <DailyLimit
            preferences={preferences}
            onUpdate={updatePreferencesSafely}
          />

        </div>
      </div>
    </div>
  );
}
